import type { DocumentType } from '@leanpay/models'

import { downloadService } from './download-service/downloadService'
import { saveBlobFile } from './downloadBlob'

/**
 * Initialize a global PDF download handler that intercepts clicks on links with
 * data-pdf-download attribute and triggers a PDF download.
 *
 * This handler uses event delegation to efficiently handle all PDF download links
 * across the application, even those added dynamically after initialization.
 */
export const initPdfDownloadHandler = (): void => {
    // Only initialize once
    if (window.__pdfDownloadHandlerInitialized) {
        return
    }

    // Mark as initialized
    window.__pdfDownloadHandlerInitialized = true

    // Add global click handler using event delegation
    document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement
        const link = target.closest('a[data-pdf-download]')

        if (!link) return

        event.preventDefault()

        const documentType = link.getAttribute('data-pdf-download') as DocumentType
        const isPersonal =
            documentType.includes('CREDIT_BUREAU_AGREEMENT') ||
            documentType.includes('FINANCIAL_DATA_AGREEMENT')

        void (async () => {
            try {
                let response
                if (isPersonal) {
                    response = await downloadService.downloadPersonalDocument(documentType)
                } else {
                    response = await downloadService.downloadGlobalDocument(documentType)
                }

                // For safari:
                // Create a blob for download with explicit application/octet-stream MIME type
                // This tells browsers to download rather than display the content
                const downloadBlob = new Blob([response.blob], {
                    type: 'application/octet-stream',
                })

                saveBlobFile(
                    downloadBlob,
                    response.fileName.includes('pdf')
                        ? response.fileName
                        : `${response.fileName || documentType}.pdf`,
                )
            } catch (error) {
                console.error('Error downloading PDF:', error)
            }
        })()
    })
}

// Add TypeScript interface for window object
declare global {
    interface Window {
        __pdfDownloadHandlerInitialized?: boolean
    }
}
